/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  DetailStack, HStack, ICON_CALENDAR_OUTLINE, Message, Skeleton, Text,
} from '@doveit/bricks';
import { formatEuro } from '@doveit/hammer';
import { useTheme } from 'styled-components';
import { isFuture } from 'date-fns';
import { Prospect } from '../../../providers/api/dtos';
import ProspectAvatar from '../../components/prospect-avatar/ProspectAvatar';
import * as styles from './ViewProspectPreview.style';
import { ReferenceType } from '../../../domain/types';
import useProspect from '../../hooks/use-prospect/useProspect';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import useLead from '../../../lead/hooks/use-lead/useLead';
import { NO_VALUE_SYMBOL } from '../../../property/utils';
import DateViewer from '../../../components/date-viewer/DateViewer';
import useContactAppointments from '../../../appointment/hooks/use-contact-appointments/useContactAppointments';
import { goToDetailPage } from '../../../utils/navigate/utils';

export type ViewProspectPreviewProps = React.AriaAttributes & ({
  prospectId: NonNullable<Prospect['id']>,
  prospect?: undefined,
} | {
  prospect: Prospect,
  prospectId?: undefined,
});

export const LOAD_PROSPECT_DATA_ERROR_MESSAGE = 'Non è stato possibile caricare i dati della valutazione';

const ViewProspectPreview: React.FC<ViewProspectPreviewProps> = ({
  prospectId,
  prospect: externalProspect,
  'aria-label': ariaLabel = 'view-prospect-preview',
  ...rest
}) => {
  const isMobile = useIsDevice('mobile');
  const { data: loadedProspect, isLoading: isProspectLoading, error: prospectError } = useProspect(prospectId);
  const theme = useTheme();

  const prospect = externalProspect || loadedProspect;
  const { data: lead, isLoading: isLeadLoading, error: leadError } = useLead(prospect?.leadId);
  const {
    data: prospectAppointments,
    isLoading: areProspectAppointmentsLoading,
    error: prospectAppointmentsErrors,
  } = useContactAppointments(prospect?.contactId, {
    referenceId: prospect?.id,
    referenceType: ReferenceType.PROSPECT,
  });
  const lastAppointment = React.useMemo(
    () => prospectAppointments?.content?.[0],
    [prospectAppointments],
  );

  const goToProspectPage = React.useCallback(() => {
    goToDetailPage(prospect!.id!, ReferenceType.PROSPECT);
  }, [prospect]);

  if ((isProspectLoading || isLeadLoading || areProspectAppointmentsLoading) && !prospectError && !leadError && !prospectAppointmentsErrors) {
    return (
      <div
        aria-label={`${ariaLabel} (skeleton)`}
        css={styles.basic}
      >
        <div css={styles.info}>
          <div>
            <Skeleton height={theme.remHeight.L} width={theme.remHeight.L} />
          </div>
          <div css={styles.textInfo}>
            <div>
              <Skeleton count={2} width="12rem" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (prospectError || leadError || prospectAppointmentsErrors) {
    return (
      <Message
        type="critical"
        message={LOAD_PROSPECT_DATA_ERROR_MESSAGE}
      />
    );
  }

  return prospect && lead && prospectAppointments && (
    <div
      {...rest}
      aria-label={ariaLabel}
      css={styles.basic}
    >
      <div
        aria-label="Area cliccabile dell'anteprima valutazione"
        onClick={goToProspectPage}
        css={styles.clickableArea}
      />
      <div css={styles.info}>
        <ProspectAvatar
          size={isMobile ? 'M' : 'L'}
        />
        <div css={styles.textInfo}>
          <Text.H4
            aria-label={`${ariaLabel} (title)`}
            lineHeight="1"
            as="div"
          >
            {lead.propertyAddress || 'Indirizzo mancante'}
          </Text.H4>
        </div>
      </div>
      <div css={styles.details}>
        <HStack gap={200}>
          <DetailStack label="Data appuntamento" icon={ICON_CALENDAR_OUTLINE}>
            {lastAppointment ? (
              <DateViewer
                stringDate={lastAppointment.startDate}
                checkIfToday
                checkIfTomorrow
                withHour={isFuture(lastAppointment.startDate)}
              />
            ) : 'Nessuno'}
          </DetailStack>
          <DetailStack label="Valutazione">
            {prospect.agentEvaluation ? formatEuro(prospect.agentEvaluation) : NO_VALUE_SYMBOL}
          </DetailStack>
        </HStack>
      </div>
    </div>
  );
};

export default ViewProspectPreview;
